// ============================================================
// จัดการห้องทั้งหมด (/project-room-all)
// ------------------------------------------------------------
// หน้านี้คือหน้า "ตั้งค่า" ของห้องทุกใบในโครงการ
//   - ลงขาย / ลงเช่า / ทั้งขายและเช่า
//   - ขายพร้อมผู้เช่า (ห้องปล่อยเช่าอยู่ แต่ขายทั้งสัญญาได้)
//   - เผยแพร่ขึ้นหน้าเว็บสาธารณะ
//   - ราคา รูป จุดเด่น สิ่งอำนวยความสะดวก การจอง หมายเหตุ
// แก้ไขทีละห้องที่หน้า /project-room-edit · ลงประกาศหลายห้องพร้อมกันผ่าน modal ในหน้านี้
//
// ★ ห้องจะขึ้นหน้าเว็บสาธารณะก็ต่อเมื่อ is_published = true เท่านั้น
//   ลงหลายห้องแบบเผยแพร่ทันที backend ตรวจความครบของข้อมูลก่อน (ที่อยู่/พื้นที่ ฯลฯ)
//   ขาดห้องไหน = ตอบ 400 พร้อมข้อความ ไม่สร้างสักห้อง (POST ของ CustomComponent เด้งข้อความนั้นให้เอง)
// ★ ปุ่มที่ต้องมีสิทธิ์เขียน ซ่อนตาม hasPermission (sale-listing.create / sale-listing.update)
//   โปรไฟล์ไม่มีข้อมูลสิทธิ์ = แสดงตามเดิม (backend ตรวจซ้ำอยู่แล้ว)
// ============================================================

import React, { Component } from "react";
import { NumericFormat } from "react-number-format";
import { saveAs } from "file-saver";
import ExcelJS from "exceljs";
import PropertyNavbar from "../../components/PropertyNavbar.js";
import InquiryModal from "../../components/InquiryModal.js";
import { stripJuristic } from "../../components/text-sanitize.js";
import {
  GET,
  POST,
  PUT,
  alert,
  loading,
  Modal,
  Pagination,
  DateTimePicker,
  getCurrentProject,
  isCustomerProfile,
  hasPermission,
} from "../../components/CustomComponent.js";
import "../../components/global.js";
import {
  LISTING_TYPES,
  PUBLISH_STATUS,
  publishStatusOf,
  liveStateOf,
  LIVE_STATE,
  publishBadgeOf,
  hasSale,
  hasRent,
  money,
  toApiDateTime,
  localDateKey,
} from "../../constants/sale.js";

import "../../assets/css/bootstrap.css";
import "../../assets/css/style.css";
import "../../assets/css/property.css";

// ตัวกรองสถานะการลงประกาศ — ป้ายใช้ชุดเดียวกับป้ายบนรูป (constants/sale.js)
// ★ "ยังไม่เผยแพร่" ในตัวกรองนี้รวมห้องที่ยังไม่ลงประกาศด้วย (คนนอกไม่เห็นเหมือนกัน)
const PUBLISH_FILTERS = [
  { value: "", label: "ทั้งหมด" },
  { value: "published", label: PUBLISH_STATUS.PUBLISHED },
  { value: "waiting", label: "รอถึงวันเริ่มประกาศ" },
  { value: "expired", label: "หมดช่วงประกาศ" },
  { value: "pending", label: PUBLISH_STATUS.PENDING },
  { value: "hidden", label: PUBLISH_STATUS.HIDDEN },
];

// ชีตของไฟล์ Export — แยกตามประเภทประกาศ (ขายพร้อมผู้เช่า = listing_type "ขาย" + sell_with_tenant ดู LISTING_CHOICES ใน project-room-edit.js)
// ★ ชีต "ขายและเช่า" (ประกาศเก่าที่เลิกใช้) กับ "ยังไม่ระบุ" (ห้องที่ยังไม่มีประกาศ) สร้างเมื่อมีห้องเท่านั้น
const EXPORT_SHEETS = [
  { name: "ขาย", always: true },
  { name: "เช่า", always: true },
  { name: "ขายพร้อมผู้เช่า", always: true },
  { name: "ขายและเช่า", always: false },
  { name: "ยังไม่ระบุ", always: false },
];

const exportSheetOf = (listing) => {
  if (!listing) return "ยังไม่ระบุ";
  if (listing.sell_with_tenant && (listing.listing_type || "ขาย") === "ขาย") return "ขายพร้อมผู้เช่า";
  return listing.listing_type || "ยังไม่ระบุ";
};

const EMPTY_SUMMARY = { total: 0, selling: 0, notSelling: 0, published: 0, pending: 0 };

// ข้อความเมื่อ backend ตอบ 403 (role ไม่มี customer.read — customer/list ตรวจสิทธิ์นี้)
const FORBIDDEN_MESSAGE = "ไม่มีสิทธิ์ดูรายการห้อง";

export default class ProjectRoomAll extends Component {
  // ★ ลูกค้าเข้าหน้านี้เพื่อจัดการห้องของตัวเองห้องเดียว
  //   การเลือกหลายห้อง / ลงประกาศหลายห้อง จึงไม่มีความหมาย
  //   (backend ปฏิเสธอยู่แล้ว — ซ่อนไว้ไม่ให้กดแล้วเจอ error เปล่า ๆ)
  // ★ ใช้ isCustomerProfile ตัวเดียวกับ PropertyNavbar/project-room-edit
  //   เดิมเช็ค user_type ตรง ๆ โปรไฟล์เก่าที่ไม่มี user_type จึงถูกมองเป็นฝ่ายขาย แต่เมนูเป็นของลูกบ้าน
  isCustomer = isCustomerProfile();

  // ★ ห้องทั้งหมดที่ผ่านตัวกรองแล้ว (ดึงครั้งเดียวต่อการค้นหา) — เปลี่ยนหน้าแค่ตัดช่วงจากชุดนี้ ไม่ยิง API ซ้ำ
  filteredRows = [];

  constructor(props) {
    super(props);
    this.state = {
      modal_bulk_sale: false,

      search: "",
      type: "",
      publish_filter: "",
      listing_filter: "",
      view: "list",        // list | grid

      // ★ เลือกได้เฉพาะห้องใน "หน้าปัจจุบัน" — เปลี่ยนหน้า/ค้นหาใหม่ = ล้างการเลือก
      //   เดิมมีโหมด "เลือกทั้งโครงการ" แต่ลงประกาศ/เผยแพร่ทำได้จริงแค่แถวที่อยู่บนจอ
      //   แถบล่างขึ้นจำนวนทั้งโครงการ ไม่ตรงกับห้องที่ถูกทำรายการจริง
      selected_ids: [],

      bulk_price: "",
      bulk_announce_start_at: "",
      bulk_announce_end_at: "",
      bulk_listing_type: "ขาย",
      bulk_rent_price: "",

      count: 0,
      page: 1,
      page_size: 15,
      data: [],
      summary: { ...EMPTY_SUMMARY },

      publishing: false,
      bulk_saving: false,
      loaded_error: "",
      list_error: "",       // โหลดรายการห้องไม่ได้ (เช่น 403) — แสดงแทน "ไม่พบห้อง"

      // นัดหมายของห้องที่กดดู (InquiryModal)
      modal_inquiry: false,
      inquiry_room: null,
      inquiries: [],

      project: null,
    };
  }

  async componentDidMount() {
    // ★ ใช้โครงการเดียวกับที่ header project-id ของ API ใช้ (cds-project)
    //   เดิมอ่าน cds-sales-project ก่อน ชื่อโครงการบนหัวหน้าอาจไม่ตรงกับข้อมูลที่ API คืนมา
    const project = getCurrentProject();

    if (!localStorage.getItem("cds-acc-tk")) {
      localStorage.clear();
      window.location.href = "/";
      return;
    }

    // ★ ลูกบ้านมีห้องเดียว หน้ารายการจึงไม่มีประโยชน์ — เด้งเข้าหน้าแก้ไขทรัพย์ของห้องตัวเองเลย
    //   (customer/list ฝั่ง backend คืนเฉพาะแถวของตัวเองอยู่แล้ว ดู ownerScopeId ใน customer.service.js)
    if (this.isCustomer) {
      await this.redirectCustomerToOwnRoom();
      return;
    }

    this.setState({ project }, async () => {
      loading(false);
      await this.Get(1);
    });
  }

  // ลูกบ้าน : ข้ามหน้ารายการไปหน้าแก้ไขทรัพย์ของห้องตัวเอง
  // ★ ใช้ replace ไม่ใช่ href — กดปุ่มย้อนกลับของเบราว์เซอร์แล้วจะได้ไม่วนกลับมาเด้งซ้ำ
  redirectCustomerToOwnRoom = async () => {
    loading(true);
    const result = await GET("api/admin/customer/list", { page: 1, page_size: 1 });
    loading(false);

    const room = result?.status ? (result.data || [])[0] : null;
    if (!room?.customer_id) {
      this.setState({ loaded_error: result?.httpStatus === 403 ? FORBIDDEN_MESSAGE : "ไม่พบห้องที่ผูกกับบัญชีนี้" });
      return;
    }
    window.location.replace(`/project-room-edit?customer_id=${encodeURIComponent(room.customer_id)}`);
  };

  // ---------- ข้อมูล ----------

  // ห้องนับว่า "ลงขาย" เมื่อมีประกาศผูกอยู่
  getSaleStatus = (item) => (item?.sale_listing ? "ลงขาย" : "ไม่ได้ลงขาย");

  getListing = (item) => item?.sale_listing || null;

  getSummary = (data) =>
    data.reduce(
      (sum, item) => {
        sum.total += 1;
        if (this.getSaleStatus(item) === "ลงขาย") sum.selling += 1;
        else sum.notSelling += 1;
        // นับเฉพาะห้องที่ขึ้นเว็บจริง (ผ่านช่วงวันประกาศด้วย)
        if (liveStateOf(item?.sale_listing) === LIVE_STATE.LIVE) sum.published += 1;
        if (publishStatusOf(item?.sale_listing) === PUBLISH_STATUS.PENDING) sum.pending += 1;
        return sum;
      },
      { ...EMPTY_SUMMARY }
    );

  // กรองฝั่งหน้าจอ (backend ยังไม่มี filter 2 ตัวนี้)
  applyLocalFilter = (rows) => {
    const { type, publish_filter, listing_filter } = this.state;
    let out = rows;
    if (type) out = out.filter((item) => this.getSaleStatus(item) === type);
    if (publish_filter === "published") out = out.filter((item) => liveStateOf(item?.sale_listing) === LIVE_STATE.LIVE);
    if (publish_filter === "waiting") out = out.filter((item) => liveStateOf(item?.sale_listing) === LIVE_STATE.WAITING);
    if (publish_filter === "expired") out = out.filter((item) => liveStateOf(item?.sale_listing) === LIVE_STATE.EXPIRED);
    if (publish_filter === "pending") out = out.filter((item) => publishStatusOf(item?.sale_listing) === PUBLISH_STATUS.PENDING);
    // ยังไม่เผยแพร่ = ห้องที่คนนอกไม่เห็น และไม่ได้อยู่ในคิวรอผู้ดูแลยืนยัน (รวมห้องที่ยังไม่ลงประกาศ)
    if (publish_filter === "hidden") {
      out = out.filter((item) => {
        const status = publishStatusOf(item?.sale_listing);
        return !status || status === PUBLISH_STATUS.HIDDEN;
      });
    }
    // ห้องที่ยังไม่มีประกาศไม่มีประเภท จึงหลุดออกเมื่อเลือกกรองประเภท
    if (listing_filter) out = out.filter((item) => (item?.sale_listing?.listing_type || "") === listing_filter);
    return out;
  };

  // ★ backend ยังไม่รองรับ filter ประเภท/สถานะการลงประกาศ ต้องกรองฝั่งหน้าจอ (applyLocalFilter)
  //   และตัวเลขสรุปด้านซ้ายต้องนับจากห้อง "ทั้งหมดที่ผ่านตัวกรอง" ไม่ใช่แค่หน้าที่แสดง
  //   จึงขอข้อมูลทั้งหมดครั้งเดียว (page=0 = ไม่แบ่งหน้า ดู customer.service.js)
  //   แล้วใช้ผลชุดเดียวกันทั้ง รายการ / จำนวน / สรุป / แบ่งหน้า
  //   เดิมยิง page=0 แยกอีกรอบเพื่อทำสรุป — มีตัวกรองอยู่ = ดึงทั้งโครงการ (พร้อมรูป) 2 รอบทุกครั้งที่ค้นหา
  Get = async (page = 1, page_size = this.state.page_size) => {
    loading(true);
    const result = await GET("api/admin/customer/list", { page: 0, data_search: this.state.search });
    loading(false);

    if (!result?.status) {
      this.filteredRows = [];
      this.setState({
        data: [],
        count: 0,
        page: 1,
        page_size,
        selected_ids: [],
        summary: { ...EMPTY_SUMMARY },
        // ★ 403 = role ไม่มีสิทธิ์ customer.read ต้องบอกตรง ๆ ไม่ใช่ "ไม่พบห้องตามเงื่อนไข" ให้ไปล้างตัวกรองเปล่า ๆ
        list_error: result?.httpStatus === 403 ? FORBIDDEN_MESSAGE : "",
      });
      return;
    }

    this.filteredRows = this.applyLocalFilter(result.data || []);
    this.setState({ summary: this.getSummary(this.filteredRows), list_error: "" });
    this.goPage(page, page_size);
  };

  // เปลี่ยนหน้า — ตัดช่วงจากผลที่กรองไว้แล้ว ไม่ยิง API ใหม่
  goPage = (page = 1, page_size = this.state.page_size) => {
    const rows = this.filteredRows;
    const lastPage = Math.max(Math.ceil(rows.length / page_size), 1);
    const current = Math.min(Math.max(page, 1), lastPage);
    this.setState({
      data: rows.slice((current - 1) * page_size, current * page_size),
      count: rows.length,
      page: current,
      page_size,
      selected_ids: [],
    });
  };

  // ---------- เลือกหลายห้อง ----------

  getItemKey = (item, index = 0) => item?.customer_id || item?.uid || `${item?.address_number || "room"}-${index}`;

  toggleSelected = (itemKey) => {
    this.setState((prev) => ({
      selected_ids: prev.selected_ids.includes(itemKey)
        ? prev.selected_ids.filter((id) => id !== itemKey)
        : [...prev.selected_ids, itemKey],
    }));
  };

  pageKeys = () => this.state.data.map((item, index) => this.getItemKey(item, index));

  // "เลือกทั้งหน้า" — ติ๊กครบแล้วกดอีกครั้ง = ล้าง (ไม่ใช่สลับไปโหมดเลือกทั้งโครงการ)
  toggleSelectAll = () => {
    const keys = this.pageKeys();
    this.setState((prev) => {
      const allOnPage = keys.length > 0 && keys.every((key) => prev.selected_ids.includes(key));
      return { selected_ids: allOnPage ? [] : keys };
    });
  };

  isSelected = (itemKey) => this.state.selected_ids.includes(itemKey);

  // checkbox เลือกห้องมีไว้ทำรายการหลายห้อง — ลูกบ้าน หรือ role ที่ไม่มีทั้งสิทธิ์สร้าง/แก้ไข ไม่ต้องเห็น
  canSelect = () =>
    !this.isCustomer && (hasPermission("sale-listing.create") || hasPermission("sale-listing.update"));

  // แถวในหน้าปัจจุบันที่ติ๊กไว้
  getSelectedRows = () => this.state.data.filter((item, index) => this.isSelected(this.getItemKey(item, index)));

  // ---------- นัดหมาย ----------
  //
  // ★ ลูกค้าไม่มีสิทธิ์ sale-inquiries.read — ปุ่มนัดหมายถูกซ่อนไปแล้ว
  //   กันไว้อีกชั้นไม่ให้ยิง API ไปโดน 403 เปล่า ๆ

  openInquiry = async (listing) => {
    if (this.isCustomer || !listing?.uid) return;
    loading(true);
    const result = await GET("api/admin/sale-inquiries", { listing_uid: listing.uid, page: 0 });
    loading(false);
    this.setState({
      modal_inquiry: true,
      inquiry_room: listing,
      inquiries: result?.status ? result.data || [] : [],
    });
  };

  closeInquiry = () => this.setState({ modal_inquiry: false, inquiry_room: null, inquiries: [] });

  // ---------- แก้ไขทรัพย์ ----------

  // ★ ย้ายไปหน้าเต็ม /project-room-edit แทนโมดัล
  //   ข้อมูลของประกาศ 1 ใบมีเยอะเกินกว่าจะกรอกในโมดัลเดียวโดยไม่ต้องเลื่อนยาว
  openSetting = (item) => {
    window.location.href = `/project-room-edit?customer_id=${encodeURIComponent(item?.customer_id || "")}`;
  };

  // ---------- เผยแพร่ ----------

  // เผยแพร่/ซ่อนเฉพาะห้องที่ติ๊กไว้
  PublishSelected = async (isPublished) => {
    const uidList = this.getSelectedListingUids();
    if (!uidList.length) {
      alert("", "warning", "แจ้งเตือน", "ห้องที่เลือกยังไม่มีประกาศขาย/เช่า");
      return;
    }

    this.setState({ publishing: true });
    loading(true);
    const result = await PUT("api/admin/sale-listings/publish", { uid_list: uidList, is_published: isPublished });
    loading(false);
    this.setState({ publishing: false });

    if (!result?.status) return;
    await this.Get(this.state.page, this.state.page_size);
    alert("", "success", "สำเร็จ", `ปรับสถานะเผยแพร่ ${result.data?.updated || 0} ห้อง`);
  };

  // ห้องที่เลือกอยู่ เอาเฉพาะที่มีประกาศแล้ว (ห้องที่ยังไม่ลงประกาศเผยแพร่ไม่ได้)
  getSelectedListingUids = () =>
    this.getSelectedRows()
      .map((item) => item?.sale_listing?.uid)
      .filter(Boolean);

  UpdateBulkSale = async () => {
    const { bulk_saving, bulk_price, bulk_announce_start_at, bulk_announce_end_at, bulk_listing_type, bulk_rent_price } = this.state;
    if (bulk_saving) return;

    if (hasSale(bulk_listing_type) && !Number(bulk_price)) {
      alert("", "warning", "แจ้งเตือน", "กรุณาระบุราคาขาย");
      return;
    }
    if (hasRent(bulk_listing_type) && !Number(bulk_rent_price)) {
      alert("", "warning", "แจ้งเตือน", "กรุณาระบุค่าเช่าต่อเดือน");
      return;
    }
    if (bulk_announce_start_at && bulk_announce_end_at && bulk_announce_end_at < bulk_announce_start_at) {
      alert("", "warning", "แจ้งเตือน", "วันที่สิ้นสุดเผยแพร่ต้องไม่น้อยกว่าวันที่เริ่มเผยแพร่");
      return;
    }

    // ตั้งค่าลงขายได้เฉพาะห้องที่ยังไม่มีประกาศ (ห้องที่มีแล้วต้องแก้ทีละห้องที่หน้าแก้ไขทรัพย์)
    const targets = this.getSelectedRows().filter((item) => !item?.sale_listing);

    if (!targets.length) {
      alert("", "warning", "แจ้งเตือน", "ห้องที่เลือกลงประกาศไว้แล้วทั้งหมด");
      return;
    }

    const body = {
      customer_list: targets.map((item) => ({
        customer_id: item.customer_id,
        listing_type: bulk_listing_type,
        price: Number(bulk_price) || 0,
        rent_price: Number(bulk_rent_price) || 0,
        rent_status: hasRent(bulk_listing_type) ? "ว่าง" : null,
        selling_status: "ว่าง",
        announce_start_at: toApiDateTime(bulk_announce_start_at),
        announce_end_at: toApiDateTime(bulk_announce_end_at),
        is_published: true,
        images: [],
      })),
    };

    // ★ กันกดซ้ำระหว่างรอ API — กดสองทีเดิมได้ error "ลงประกาศซ้ำ" จากคำขอที่สอง
    this.setState({ bulk_saving: true });
    loading(true);
    const result = await POST("api/admin/sale-listings", body);
    loading(false);
    this.setState({ bulk_saving: false });

    // ★ ไม่สำเร็จ (เช่น 400 ข้อมูลห้องไม่ครบสำหรับเผยแพร่) POST แสดงข้อความจาก backend ให้แล้ว
    //   คงโมดัลไว้ ค่าที่กรอกไม่หาย ผู้ใช้อ่านข้อความแล้วตัดสินใจต่อได้
    if (!result?.status) return;

    await this.Get(this.state.page, this.state.page_size);
    this.setState({ modal_bulk_sale: false });
    alert("", "success", "สำเร็จ", `ลงประกาศ ${targets.length} ห้องเรียบร้อย`);
  };

  // ---------- แสดงผล ----------

  // ชิปสรุปตัวกรองที่ใช้อยู่ — กด x เพื่อถอดทีละเงื่อนไข
  filterChips = () => {
    const { search, type, publish_filter, listing_filter } = this.state;
    const chips = [];
    if (search) chips.push({ text: `ค้นหา: ${search}`, patch: { search: "" } });
    if (type) chips.push({ text: type === "ลงขาย" ? "ลงประกาศแล้ว" : "ยังไม่ลงประกาศ", patch: { type: "" } });
    if (listing_filter) chips.push({ text: `ประเภท: ${listing_filter}`, patch: { listing_filter: "" } });
    if (publish_filter) {
      chips.push({ text: PUBLISH_FILTERS.find((i) => i.value === publish_filter)?.label || "", patch: { publish_filter: "" } });
    }
    return chips;
  };

  removeChip = (patch) => this.setState(patch, () => this.Get(1, this.state.page_size));

  clearAll = () =>
    this.setState({ search: "", type: "", publish_filter: "", listing_filter: "" }, () => this.Get(1, this.state.page_size));

  // ---------- Export Excel ----------
  // ★ ส่งออกทุกห้องในโครงการเสมอ ไม่สนตัวกรองบนหน้าจอ — ดึงใหม่ด้วย page=0 (ไม่แบ่งหน้า ไม่ส่งคำค้น) ไม่ใช้ this.filteredRows
  exportExcel = async () => {
    loading(true);
    try {
      const result = await GET("api/admin/customer/list", { page: 0 });
      if (!result?.status || !Array.isArray(result.data)) {
        alert("", "warning", "แจ้งเตือน", "โหลดข้อมูลสำหรับ Export ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
        return;
      }
      if (result.data.length === 0) {
        alert("", "warning", "แจ้งเตือน", "ไม่พบข้อมูลสำหรับ Export");
        return;
      }

      const groups = Object.fromEntries(EXPORT_SHEETS.map((s) => [s.name, []]));
      result.data.forEach((item) => {
        const listing = this.getListing(item);
        const key = exportSheetOf(listing);
        (groups[key] || groups["ยังไม่ระบุ"]).push({ item, listing });
      });

      const wb = new ExcelJS.Workbook();
      EXPORT_SHEETS.filter((s) => s.always || groups[s.name].length).forEach((sheet) => {
        const ws = wb.addWorksheet(sheet.name);
        ws.columns = [
          { header: "เลขห้อง", key: "address_number", width: 15 },
          { header: "เจ้าของ", key: "owner", width: 30 },
          { header: "ประเภทประกาศ", key: "listing_type", width: 20 },
          { header: "ราคาขาย", key: "price", width: 16, style: { numFmt: "#,##0" } },
          { header: "ค่าเช่า", key: "rent_price", width: 16, style: { numFmt: "#,##0" } },
          { header: "สถานะ", key: "status", width: 20 },
          { header: "สถานะเผยแพร่", key: "publish", width: 18 },
        ];

        const headerRow = ws.getRow(1);
        headerRow.height = 25;
        headerRow.eachCell((cell) => {
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF198754" } };
          cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
          cell.alignment = { vertical: "middle", horizontal: "center" };
        });

        groups[sheet.name].forEach(({ item, listing }) => {
          const type = listing?.listing_type || "";
          const sellSide = !!listing && hasSale(type || "ขาย");
          const rentSide = !!listing && (hasRent(type) || !!listing.sell_with_tenant);
          let status = "-";
          if (listing) {
            if (type === "ขายและเช่า") status = `ขาย: ${listing.selling_status || "-"} / เช่า: ${listing.rent_status || "-"}`;
            else if (type === "เช่า") status = listing.rent_status || "-";
            else status = listing.selling_status || "-";
          }
          const row = ws.addRow({
            address_number: item.address_number || "",
            owner: item.full_name || "",
            listing_type: listing ? (sheet.name === "ขายพร้อมผู้เช่า" ? "ขายพร้อมผู้เช่า" : type || "ขาย") : "ยังไม่ลงประกาศ",
            price: sellSide && listing.price != null && listing.price !== "" ? Number(listing.price) : "",
            rent_price: rentSide && Number(listing.rent_price) ? Number(listing.rent_price) : "",
            status,
            publish: publishBadgeOf(listing).label,
          });
          row.height = 22;
          row.alignment = { vertical: "middle" };
        });
      });

      const projectName = (stripJuristic(this.state.project?.project_name) || "โครงการ").replace(/[\\/:*?"<>|]/g, "").trim();
      const buffer = await wb.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), `รายการจัดการทรัพย์_${projectName}_${localDateKey()}.xlsx`);
    } catch (e) {
      console.error("exportExcel error:", e);
      alert("", "warning", "แจ้งเตือน", "Export ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
    } finally {
      loading(false);
    }
  };

  // ---------- ตัวกรอง ----------
  renderFilterPanel = () => {
    const { search, publish_filter, listing_filter } = this.state;

    return (
      <div className="prop-card prop-in mb-3">
        <div className="prop-card__head">
          <span className="prop-title text-16">
            <span className="prop-title__dot" />
            ค้นหาทรัพย์
          </span>
          <button type="button" className="prop-btn prop-btn--ghost prop-btn--sm" onClick={this.clearAll}>
            ล้างค่า
          </button>
        </div>

        <div className="p-3">
          <div className="row g-3">
            <div className="col-12 col-lg-6 col-xl-6 col-xxl-4 col-xxxl-3">
              <label className="prop-label">ค้นหา</label>
              <input
                className="prop-field"
                placeholder="ชื่อ-สกุล, บ้านเลขที่, เบอร์โทร"
                value={search}
                onChange={(e) => this.setState({ search: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && this.Get(1, this.state.page_size)}
              />
            </div>

            <div className="col-12 col-lg-6 col-xl-6 col-xxl-4 col-xxxl-3">
              <label className="prop-label">ประเภทประกาศ</label>
              <select
                className="prop-field prop-select"
                value={listing_filter}
                onChange={(e) => this.setState({ listing_filter: e.target.value }, () => this.Get(1, this.state.page_size))}
              >
                <option value="">ทุกประเภท</option>
                {LISTING_TYPES.map((i) => (
                  <option key={i} value={i}>{i}</option>
                ))}
              </select>
            </div>


            <div className="col-12 col-lg-12 col-xl-6 col-xxl-4 col-xxxl-3">
              <label className="prop-label">สถานะการลงประกาศ</label>
              {/* ปุ่มแบ่งช่อง อ่านเร็วกว่า dropdown
                  ★ "รอเผยแพร่" = ลูกค้าลงประกาศมาแล้ว รอผู้ดูแลกดอนุมัติ ยังไม่ขึ้นหน้าเว็บ */}
              <div className="prop-seg">
                {PUBLISH_FILTERS.map((i) => (
                  <button
                    key={i.value || "all"}
                    type="button"
                    className={`prop-seg__btn ${publish_filter === i.value ? "is-active" : ""}`}
                    onClick={() => this.setState({ publish_filter: i.value }, () => this.Get(1, this.state.page_size))}
                  >
                    {i.label}
                  </button>
                ))}
              </div>
            </div>

          </div>

          <div className="d-flex justify-content-end gap-2 mt-3">
            <button type="button" className="prop-btn prop-btn--ghost" onClick={this.exportExcel}>
              <span className="icon text-14">{"\uf019"}</span> Export Excel
            </button>
            <button type="button" className="prop-btn" onClick={() => this.Get(1, this.state.page_size)}>
              <span className="icon text-14">{""}</span> ค้นหา
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ---------- แถบเครื่องมือเหนือรายการ ----------
  renderToolbar = () => {
    const { count, view } = this.state;
    const pageKeys = this.pageKeys();
    const allOnPage = pageKeys.length > 0 && pageKeys.every((key) => this.isSelected(key));

    return (
      <div className="prop-card prop-in p-3 mb-3">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div className="d-flex align-items-center gap-3">
            {this.canSelect() && (
              <label className="d-flex align-items-center gap-2 mb-0 pointer" title="เลือกทั้งหน้า (เฉพาะห้องที่แสดงอยู่ในหน้านี้)">
                <input
                  type="checkbox"
                  className="form-check-input mt-0"
                  style={{ accentColor: "var(--prop-lime-lo)" }}
                  checked={allOnPage}
                  onChange={this.toggleSelectAll}
                />
              </label>
            )}
            <div>
              <span className="fw-bold text-dark">ผลการค้นหา</span>
              <span className="fw-bold ms-2" style={{ color: "#4d7c0f" }}>{Number(count || 0).toLocaleString("th-TH")}</span>
              <span className="text-secondary text-12 ms-1">รายการ</span>
            </div>
          </div>

          <div className="d-flex flex-wrap align-items-center gap-2">
            <div className="prop-toggle">
              <button
                type="button"
                title="มุมมองรายการ"
                className={`prop-toggle__btn icon ${view === "list" ? "is-active" : ""}`}
                onClick={() => this.setState({ view: "list" })}
              >
                {""}
              </button>
              <button
                type="button"
                title="มุมมองตาราง"
                className={`prop-toggle__btn icon ${view === "grid" ? "is-active" : ""}`}
                onClick={() => this.setState({ view: "grid" })}
              >
                {""}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ---------- แถบการกระทำเมื่อเลือกห้องไว้ ----------
  renderBulkBar = () => {
    const { selected_ids, publishing } = this.state;
    const selectedCount = selected_ids.length;
    if (!this.canSelect() || !selectedCount) return null;
    const canCreate = hasPermission("sale-listing.create");
    const canUpdate = hasPermission("sale-listing.update");

    return (
      <div className="prop-bulk prop-in mb-3">
        <div className="d-flex align-items-center gap-2 text-14 fw-semibold" style={{ color: "var(--prop-ink)" }}>
          <span className="icon">{""}</span>
          เลือกแล้ว {selectedCount} ห้อง
          <span className="text-secondary text-12 fw-normal">(ในหน้านี้)</span>
        </div>
        <div className="d-flex flex-wrap gap-2">
          {canCreate && (
            <button type="button" className="prop-btn prop-btn--sm" onClick={() => this.setState({ modal_bulk_sale: true })}>
              <span className="icon text-12">{""}</span> ลงประกาศ
            </button>
          )}
          {/* ★ ใช้คำชุดเดียวกับป้ายสถานะบนการ์ด : เผยแพร่ / ปิดเผยแพร่
              เดิมเขียนว่า "ซ่อน" ซึ่งไม่ตรงกับป้ายไหนในระบบแล้ว */}
          {canUpdate && (
            <button type="button" className="prop-btn prop-btn--sm" disabled={publishing} onClick={() => this.PublishSelected(true)}>
              <span className="icon text-12">{""}</span> เผยแพร่
            </button>
          )}
          {canUpdate && (
            <button
              type="button"
              className="prop-btn prop-btn--ghost prop-btn--sm"
              disabled={publishing}
              onClick={() => this.PublishSelected(false)}
            >
              <span className="icon text-12">{""}</span> ปิดเผยแพร่
            </button>
          )}
          <button
            type="button"
            className="prop-btn prop-btn--ghost prop-btn--sm"
            onClick={() => this.setState({ selected_ids: [] })}
          >
            <span className="icon text-12">{""}</span> ล้างการเลือก
          </button>
        </div>
      </div>
    );
  };

  // ---------- แถบด้านซ้าย : ชิปตัวกรอง + สรุปตัวเลข ----------
  renderSidebar = () => {
    const { summary } = this.state;
    const chips = this.filterChips();

    return (
      <div className="d-flex flex-column gap-3" style={{ position: "sticky", top: "82px" }}>
        <div className="prop-card prop-in">
          <div className="prop-card__head py-3 px-3">
            <span className="prop-title text-14">
              <span className="prop-title__dot" />
              เงื่อนไขที่ใช้อยู่
            </span>
            {!!chips.length && (
              <button type="button" className="prop-link" onClick={this.clearAll}>ล้างทั้งหมด</button>
            )}
          </div>
          <div className="p-3">
            {chips.length ? (
              <div className="d-flex flex-wrap gap-2">
                {chips.map((chip, i) => (
                  <span key={i} className="prop-chip">
                    <span className="text-truncate" style={{ maxWidth: "150px" }}>{chip.text}</span>
                    <button type="button" className="prop-chip__x icon" title="เอาออก" onClick={() => this.removeChip(chip.patch)}>
                      {""}
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <div className="text-secondary text-12 text-center py-2">ยังไม่ได้ใส่เงื่อนไข</div>
            )}
          </div>
        </div>

        <div className="prop-card prop-in">
          <div className="prop-card__head py-3 px-3">
            <span className="prop-title text-14">
              <span className="prop-title__dot" />
              สรุปทรัพย์
            </span>
          </div>
          <div className="p-3">
            {[
              { label: "ห้องทั้งหมด", value: summary.total },
              { label: "ลงประกาศแล้ว", value: summary.selling },
              { label: PUBLISH_STATUS.PENDING, value: summary.pending },
              { label: "ยังไม่ลงประกาศ", value: summary.notSelling },
              { label: `${PUBLISH_STATUS.PUBLISHED} (ขึ้นหน้าเว็บ)`, value: summary.published },
            ].map((row) => (
              <div key={row.label} className="d-flex align-items-center justify-content-between py-1">
                <span className="text-secondary text-12">{row.label}</span>
                <b>{row.value}</b>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // ---------- ทรัพย์ 1 ใบ ----------
  renderItem = (item, index) => {
    const { view } = this.state;
    const itemKey = this.getItemKey(item, index);
    const checked = this.isSelected(itemKey);
    const listing = this.getListing(item);
    const publishStatus = publishStatusOf(listing);
    const status = publishBadgeOf(listing);
    const images = [...(listing?.images || [])].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
    const canCreate = hasPermission("sale-listing.create");
    const canUpdate = hasPermission("sale-listing.update");
    const cover = images[0]?.image_url || "";

    const spec = (icon, value, key) => (
      <span className="prop-spec" key={key}>
        <span className="icon">{icon}</span>
        {value}
      </span>
    );

    return (
      <div
        key={`room-all-${itemKey}`}
        className={`prop-item overflow-hidden ${checked ? "is-checked" : ""}`}
      >
        {/* รูปทรัพย์ — รูปแรกตามลำดับที่ตั้งไว้ ไม่มีก็โชว์ไอคอนแทน
            ★ customer/list ส่งรูปมาเป็น object {image_url, sequence} ไม่ใช่ string
              (คนละรูปแบบกับ DTO ของ sale-listings ที่ส่งเป็น array ของ URL) */}
        <div className="prop-thumb">
          {cover ? (
            <img src={cover} alt={item.address_number} className="prop-thumb__img" />
          ) : (
            <span className="prop-thumb__ghost icon">{""}</span>
          )}
          <span className="prop-thumb__scrim" />
          <span className="prop-thumb__badge" style={{ backgroundColor: status.tint, color: status.color }}>
            {status.label}
          </span>
          {!!listing?.images?.length && (
            <span className="prop-thumb__count">
              <span className="icon text-10">{""}</span>
              {listing.images.length}
            </span>
          )}
          {view === "grid" && this.canSelect() && (
            <span className="prop-item__check">
              <input type="checkbox" checked={checked} onChange={() => this.toggleSelected(itemKey)} />
            </span>
          )}
        </div>

        {/* ข้อมูลทรัพย์ */}
        <div className="prop-item__body">
          <div className="prop-item__title-row d-flex align-items-start flex-wrap gap-2">
            <span className="prop-item__name pointer" onClick={() => this.openSetting(item)}>
              ห้อง {item.address_number || "-"}
            </span>
            {!!listing && <span className="prop-tag">{listing.listing_type || "ขาย"}</span>}
            {/* ผู้ซื้อเข้าอยู่เองไม่ได้ทันที ต้องเห็นตั้งแต่ในรายการ */}
            {!!listing?.sell_with_tenant && (
              <span className="prop-tag" style={{ background: "#fff2d9", borderColor: "#ffe0a3", color: "#8a5a00" }}>
                ขายพร้อมผู้เช่า
              </span>
            )}
            {/* ★ สถานะห้องขึ้นเป็นแท็กข้างชื่อ ไม่ปนกับสเปก (อาคาร/ชั้น/พื้นที่)
                แถวสเปกจะได้อ่านรวดเดียวว่าห้องหน้าตาแบบไหน */}
            {!!listing?.selling_status && <span className="prop-tag">{listing.selling_status}</span>}
            {!!listing?.rent_status && listing.rent_status !== "ว่าง" && (
              <span className="prop-tag">เช่า: {listing.rent_status}</span>
            )}
          </div>

          <div className="prop-addr mt-2">
            <span className="icon text-10">{""}</span>
            <span className="text-truncate">{item.full_name || "ไม่ระบุเจ้าของห้อง"}</span>
          </div>

          <div className="prop-specs mt-2">
            {!!item.building && spec("", `อาคาร ${item.building}`, "b")}
            {!!item.floor && spec("", `ชั้น ${item.floor}`, "f")}
            {!!item.room_size && spec("", `${item.room_size} ตร.ม.`, "s")}
          </div>

          <div className="d-flex flex-wrap align-items-baseline gap-3 mt-2">
            {!!listing && hasSale(listing.listing_type || "ขาย") && (
              <span className="prop-price prop-price--sale">
                ขาย {money(listing.price)} <span className="prop-price__unit">บาท</span>
              </span>
            )}
            {/* ค่าเช่า 0 = ยังไม่ได้ตั้งราคา ไม่ต้องโชว์ (เดิมขึ้น "เช่า 0 บาท/เดือน" อ่านแล้วเข้าใจผิด) */}
            {!!listing && (hasRent(listing.listing_type) || listing.sell_with_tenant) && !!Number(listing.rent_price) && (
              <span className="prop-price">
                เช่า {money(listing.rent_price)} <span className="prop-price__unit">บาท/เดือน</span>
              </span>
            )}
            {!listing && <span className="text-secondary text-12">ยังไม่ได้ลงประกาศ</span>}
          </div>
        </div>

        {/* สถานะ + ปุ่มจัดการ */}
        <div className="prop-item__side">
          <div className="d-flex align-items-start justify-content-between gap-2">
            <div className="d-flex flex-column gap-2 overflow-hidden">
              {!!listing && (
                <div className="prop-meta">
                  <span className="icon">{""}</span>
                  <span>ดูแล้ว {money(listing.view_count)} ครั้ง</span>
                </div>
              )}
              {/* ★ มุมมองการ์ดโชว์แค่ยอดเข้าชม — จุดเด่นเป็นข้อความยาว ทำให้การ์ดสูงไม่เท่ากัน */}
              {!!listing?.highlight && view === "list" && (
                <div className="prop-meta">
                  <span className="icon">{""}</span>
                  <span className="text-truncate">{listing.highlight}</span>
                </div>
              )}
            </div>
            {view === "list" && this.canSelect() && (
              <span className="prop-item__check">
                <input type="checkbox" checked={checked} onChange={() => this.toggleSelected(itemKey)} />
              </span>
            )}
          </div>

          <div className="d-flex align-items-center justify-content-end gap-2 mt-auto flex-wrap">
            {/* ปุ่มนัดหมาย : มีคำขอใหม่ = ปุ่มแดง + ป้ายตัวเลข
                ★ ต้องมีสิทธิ์ sale-inquiries.read (ไม่มี = เปิดแล้วโดน 403 จึงซ่อน) */}
            {!this.isCustomer && hasPermission("sale-inquiries.read") && !!listing?.inquiry_count && (
              <button
                type="button"
                className={`prop-btn prop-btn--sm position-relative ${listing.inquiry_new_count ? "" : "prop-btn--ghost"}`}
                style={listing.inquiry_new_count ? { background: "#dc3545", boxShadow: "none" } : undefined}
                title={`นัดหมาย ${listing.inquiry_count} รายการ`}
                onClick={() => this.openInquiry({ ...listing, address_number: item.address_number })}
              >
                <span className="icon text-12">{"\uf073"}</span>
                {/* การ์ดมีที่จำกัด — เหลือไอคอนพอ ตัวเลขคำขอใหม่ยังอยู่บนป้ายมุมขวา */}
                {view === "list" && "นัดหมาย"}
                {!!listing.inquiry_new_count && (
                  <span
                    className="position-absolute translate-middle badge rounded-pill bg-danger border border-white"
                    style={{ top: 0, left: "100%" }}
                  >
                    {listing.inquiry_new_count}
                  </span>
                )}
              </button>
            )}
            {/* ★ การอนุมัติเผยแพร่ย้ายไปทำที่หน้ารายละเอียดทรัพย์แล้ว (ปุ่ม "อนุมัติ (เผยแพร่)" ในแถบล่าง)
                ผู้ดูแลจะได้เห็นข้อมูลทั้งใบก่อนกด ไม่ใช่กดอนุมัติจากรายการโดยยังไม่ได้อ่าน */
            }
            {/* ลูกค้ากดเผยแพร่เองไม่ได้ — บอกให้รู้ว่ากำลังรออะไรอยู่ */}
            {this.isCustomer && publishStatus === PUBLISH_STATUS.PENDING && (
              <span className="text-12" style={{ color: "#8a5a00" }}>รอผู้ดูแลยืนยัน</span>
            )}
            {/* ประวัติของห้องนี้ — ใช้หน้ารายการประวัติเดิม กรองด้วย listing_uid
                ★ ลูกบ้านไม่มีสิทธิ์ sale-listing-logs.read กดแล้วจะโดน 403 จึงซ่อนไว้ */}
            {!!listing && !this.isCustomer && hasPermission("sale-listing-logs.read") && (
              <button
                type="button"
                className="prop-iconbtn icon"
                title="ดูประวัติการทำรายการของห้องนี้"
                onClick={() => (window.location.href = `/sale-listing-logs?listing_uid=${encodeURIComponent(listing.uid)}`)}
              >
                {"\uf1da"}
              </button>
            )}
            {/* ★ ห้องที่ยังไม่มีประกาศ + ไม่มีสิทธิ์สร้าง = ไม่มีอะไรให้ดู ซ่อนปุ่ม
                ห้องที่มีประกาศแล้วยังเปิดดูรายละเอียดได้ (หน้าแก้ไขซ่อนปุ่มบันทึกเองตามสิทธิ์) */}
            {(listing || canCreate) && (
              <button
                type="button"
                className="prop-iconbtn prop-iconbtn--dark icon"
                title={listing ? (canUpdate ? "ตั้งค่าห้อง" : "ดูรายละเอียดห้อง") : "ลงประกาศห้องนี้"}
                onClick={() => this.openSetting(item)}
              >
                {listing ? "" : ""}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  renderResults = () => {
    const { data, view, count, page, page_size, list_error } = this.state;

    if (list_error) {
      return (
        <div className="prop-card prop-in">
          <div className="prop-empty">
            <div className="prop-empty__icon icon">{"\uf023"}</div>
            <div className="fw-bold text-dark mb-1">{list_error}</div>
            <div className="text-12">กรุณาติดต่อผู้ดูแลระบบให้เพิ่มสิทธิ์ "ลูกค้าโครงการ (ดู)" ให้บทบาทของคุณ</div>
          </div>
        </div>
      );
    }

    if (!data.length) {
      return (
        <div className="prop-card prop-in">
          <div className="prop-empty">
            <div className="prop-empty__icon icon">{""}</div>
            <div className="fw-bold text-dark mb-1">ไม่พบห้องตามเงื่อนไข</div>
            <div className="text-12">ลองล้างตัวกรองแล้วค้นหาใหม่</div>
            <button type="button" className="prop-btn prop-btn--ghost prop-btn--sm mt-3" onClick={this.clearAll}>
              ล้างทั้งหมด
            </button>
          </div>
        </div>
      );
    }

    const from = (page - 1) * page_size + 1;
    const to = Math.min(page * page_size, count);

    return (
      <>
        <div className={view === "grid" ? "prop-grid" : "prop-list"}>
          {data.map((item, index) => this.renderItem(item, index))}
        </div>

        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mt-4 text-secondary text-12">
          <div>
            แสดง {from}-{to} จาก {money(count)} รายการ
          </div>
          <Pagination
            className="mb-0"
            page={page}
            count={Math.ceil(count / page_size) || 1}
            onChange={(_, next) => {
              this.goPage(next, page_size);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          />
        </div>
      </>
    );
  };

  render() {
    // ลูกบ้านไม่มีหน้ารายการ — โชว์แค่สถานะระหว่างเด้งไปหน้าแก้ไขทรัพย์
    if (this.isCustomer) {
      return (
        <div className="w-100 min-vh-100 prop-page">
          <PropertyNavbar menuKey="sales-room-all" page1="จัดการทรัพย์" page2="จัดการทรัพย์" />
          <div className="body p-4">
            <div className="prop-card prop-in p-5 text-center text-secondary">
              {this.state.loaded_error || "กำลังเปิดข้อมูลทรัพย์ของคุณ..."}
            </div>
          </div>
        </div>
      );
    }

    const {
      modal_bulk_sale,
      bulk_price,
      bulk_announce_start_at,
      bulk_announce_end_at,
      bulk_listing_type,
      bulk_rent_price,
      selected_ids,
      bulk_saving,
      project,
      modal_inquiry,
      inquiry_room,
      inquiries,
    } = this.state;

    const selectedCount = selected_ids.length;

    return (
      <div className="w-100 min-vh-100 prop-page">
        <PropertyNavbar menuKey="sales-room-all" page1="จัดการทรัพย์" page2="จัดการทรัพย์" projectName={project?.project_name} />

        <div className="body p-4">
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3 prop-in">
            <div>
              <h3 className="fw-bold mb-1 text-dark">จัดการทรัพย์</h3>
              <div className="text-secondary text-14">
                {stripJuristic(project?.project_name) || "-"} · ลงประกาศขาย/ให้เช่า และคุมว่าห้องไหนขึ้นหน้าเว็บ
              </div>
            </div>
          </div>

          {this.renderFilterPanel()}
          {this.renderToolbar()}
          {this.renderBulkBar()}

          <div className="row g-3">
            <div className="col-xl-3 col-lg-4">{this.renderSidebar()}</div>
            <div className="col-xl-9 col-lg-8">{this.renderResults()}</div>
          </div>
        </div>

        {/* ---------- modal ลงประกาศหลายห้อง ---------- */}
        <Modal show={modal_bulk_sale} onHide={() => this.setState({ modal_bulk_sale: false })}>
          <Modal.Header closeButton>
            <h5 className="fw-bold mb-0">ลงประกาศหลายห้อง</h5>
          </Modal.Header>
          <Modal.Body>
            <div className="bg-light rounded-4 p-3 mb-3">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                  <div className="fw-bold">ห้องที่เลือก</div>
                  <small className="text-secondary">ใช้ค่าเดียวกันกับทุกห้องที่ยังไม่มีประกาศ</small>
                </div>
                <span className="bg-success-light text-success rounded-pill px-3 py-2 fw-bold">{selectedCount} ห้อง</span>
              </div>
            </div>

            <div className="mb-3">
              <label className="fw-bold mb-2">ประเภทประกาศ</label>
              <div className="d-flex flex-wrap gap-2">
                {LISTING_TYPES.map((item) => (
                  <button
                    key={item}
                    className={`btn px-3 ${bulk_listing_type === item ? "btn-success" : "btn-outline-success"}`}
                    onClick={() => this.setState({ bulk_listing_type: item })}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>

            {hasSale(bulk_listing_type) && (
              <div className="mb-3">
                <label className="fw-bold mb-2">ราคาขาย</label>
                {/* ★ ช่องตัวเลขใช้ NumericFormat — คั่นหลักพันให้อ่านง่าย ค่าที่เก็บยังเป็นตัวเลขล้วน */}
                <NumericFormat
                  type="text"
                  inputMode="decimal"
                  thousandSeparator=","
                  allowNegative={false}
                  decimalScale={2}
                  className="form-control"
                  value={bulk_price}
                  onValueChange={(v) => this.setState({ bulk_price: v.value })}
                />
              </div>
            )}

            {hasRent(bulk_listing_type) && (
              <div className="mb-3">
                <label className="fw-bold mb-2">ค่าเช่าต่อเดือน</label>
                {/* ★ ช่องตัวเลขใช้ NumericFormat — คั่นหลักพันให้อ่านง่าย ค่าที่เก็บยังเป็นตัวเลขล้วน */}
                <NumericFormat
                  type="text"
                  inputMode="decimal"
                  thousandSeparator=","
                  allowNegative={false}
                  decimalScale={2}
                  className="form-control"
                  value={bulk_rent_price}
                  onValueChange={(v) => this.setState({ bulk_rent_price: v.value })}
                />
              </div>
            )}

            <div className="row g-2 mb-2">
              <div className="col-12 col-md-6">
                <label className="fw-bold mb-2">วันที่เริ่มเผยแพร่</label>
                {/* ★ ใช้ DateTimePicker กลาง ไม่ใช้ datetime-local — native แสดงวันที่ตาม locale เบราว์เซอร์ */}
                <DateTimePicker
                  value={bulk_announce_start_at}
                  onChange={(v) => this.setState({ bulk_announce_start_at: v })}
                />
              </div>
              <div className="col-12 col-md-6">
                <label className="fw-bold mb-2">วันที่สิ้นสุดเผยแพร่</label>
                <DateTimePicker
                  value={bulk_announce_end_at}
                  onChange={(v) => this.setState({ bulk_announce_end_at: v })}
                />
              </div>
            </div>

            <div className="text-secondary text-12 mt-3">
              ห้องที่ลงประกาศไว้แล้วจะถูกข้าม ต้องแก้ทีละห้องผ่านปุ่มตั้งค่า · ประกาศที่สร้างจะเผยแพร่บนหน้าเว็บทันที ·
              ห้องที่ข้อมูลยังไม่ครบ (เช่น ที่อยู่ / พื้นที่ใช้สอย) ระบบจะไม่ให้ลงประกาศและแจ้งรายการที่ต้องเติม
            </div>
          </Modal.Body>
          <Modal.Footer className="py-4">
            <div className="w-100 d-flex">
              <button className="btn btn-outline-success w-100 me-1" onClick={() => this.setState({ modal_bulk_sale: false })}>
                ยกเลิก
              </button>
              <button className="btn btn-success w-100 ms-1" disabled={bulk_saving} onClick={this.UpdateBulkSale}>
                {bulk_saving ? "กำลังลงประกาศ..." : "ยืนยันการลงประกาศ"}
              </button>
            </div>
          </Modal.Footer>
        </Modal>

        {/* ---------- นัดหมายของห้องนี้ ----------
            ★ ใช้คอมโพเนนต์เดียวกับหน้า /sale-inquiries (components/InquiryModal.js)
              ฟอร์มติดตามมีหลายช่อง ถ้าก๊อปสองที่ อีกหน้าจะตกรุ่นเงียบ ๆ */}
        <InquiryModal
          show={modal_inquiry}
          roomLabel={inquiry_room?.address_number}
          inquiries={inquiries}
          onClose={this.closeInquiry}
          onSaved={() => this.Get(this.state.page, this.state.page_size)}
        />

      </div>
    );
  }
}

